// TripDetails.js (Portal) — version à jour avec TripProgramCard (components-lib)
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { SectionHeader, Text, TripProgramCard } from "@zorni/components-lib";
import { useTranslation } from "react-i18next";

import ModalTripTimelineItem from "../modalTripTimelineItem/ModalTripTimelineItem";
import AcquisitionsHelper from "./AcquisitionsHelper";
import styles from "./TripDetails.module.css";
import { currentLocale } from "../../../../common/i18n/locale";

const TripDetails = ({
  tripProgramDetails,
  setAcquisitions,
  acquisitions,
  selectedTripDateSummary,
}) => {
  const { t } = useTranslation();

  const [sortedTripProgramDetails, setSortedTripProgramDetails] = useState([]);
  const [selectedTrip, setSelectedTrip] = useState(null);

  const sortTripProgramByRanking = useCallback((items) => {
    if (!Array.isArray(items)) return [];
    return [...items].sort((a, b) => {
      const rankA = a?.ranking?.rank ?? Infinity;
      const rankB = b?.ranking?.rank ?? Infinity;
      return rankA - rankB;
    });
  }, []);

  useEffect(() => {
    setSortedTripProgramDetails(sortTripProgramByRanking(tripProgramDetails));
  }, [tripProgramDetails, sortTripProgramByRanking]);

  const openModal = useCallback(
    (tripProgram, index) => setSelectedTrip({ trip: tripProgram, index }),
    []
  );
  const closeModal = useCallback(() => setSelectedTrip(null), []);

  const items = useMemo(
    () =>
      Array.isArray(sortedTripProgramDetails) ? sortedTripProgramDetails : [],
    [sortedTripProgramDetails]
  );

  const getTripActivities = useCallback((trip) => {
    if (Array.isArray(trip?.activities) && trip.activities.length) return trip.activities;
    if (Array.isArray(trip?.activites) && trip.activites.length) return trip.activites;
    return [];
  }, []);

  const stepDateByStepId = useMemo(() => {
    const index = new Map();
    const stepsDate = selectedTripDateSummary?.stepsDate ?? [];
    const uniqueDates = new Set();

    stepsDate.forEach((stepDate) => {
      if (stepDate?.date) uniqueDates.add(stepDate.date);
      (stepDate?.stepIds ?? []).forEach((stepId) => {
        if (!index.has(stepId)) {
          index.set(stepId, stepDate);
        }
      });
    });

    const shouldFallbackToSequentialDates =
      !!selectedTripDateSummary?.startDate &&
      (uniqueDates.size <= 1 || index.size < items.length);

    if (shouldFallbackToSequentialDates) {
      const startDate = new Date(selectedTripDateSummary.startDate);
      if (!Number.isNaN(startDate.getTime())) {
        items.forEach((trip, tripIndex) => {
          if (!trip?.id) return;
          if (uniqueDates.size > 1 && index.has(trip.id)) return;
          const stepDate = new Date(startDate);
          stepDate.setUTCDate(stepDate.getUTCDate() + tripIndex);
          index.set(trip.id, { date: stepDate.toISOString().slice(0, 10) });
        });
      }
    }

    return index;
  }, [selectedTripDateSummary, items]);

  /* Date d'une étape pour la colonne du rail : « 12 », « Oct. », « Lundi ». */
  const splitStepDate = useCallback((rawDate) => {
    if (!rawDate) return null;
    const d = new Date(rawDate);
    if (Number.isNaN(d.getTime())) return null;
    const part = (options) => new Intl.DateTimeFormat(currentLocale(), { timeZone: "UTC", ...options }).format(d);
    return {
      day: part({ day: "2-digit" }),
      month: part({ month: "short" }).replace(".", ""),
      weekday: part({ weekday: "long" }),
      full: part({ weekday: "short", day: "2-digit", month: "short", year: "numeric" }),
    };
  }, []);

  const titlesOf = (list) =>
    (Array.isArray(list) ? list : [])
      .map((entry) => (typeof entry === "string" ? entry : entry?.title))
      .filter(Boolean);

  const stats = useMemo(() => {
    const countries = [...new Set(items.map((trip) => trip?.country).filter(Boolean))];
    return [
      { value: items.length, label: t("tripDetails.timeline.steps", "Étapes") },
      {
        value: selectedTripDateSummary?.nbDays || items.length,
        label: t("tripDetails.timeline.days", "Jours"),
      },
      countries.length
        ? { value: countries.join(" · "), label: t("tripDetails.timeline.destination", "Destination") }
        : null,
    ].filter(Boolean);
  }, [items, selectedTripDateSummary, t]);

  return (
    <section className={styles.section} aria-labelledby="trip-timeline-title">
      <div className={styles.header}>
        <SectionHeader
          titleId="trip-timeline-title"
          kicker={t("tripDetails.timeline.kicker", "Programme")}
          title={
            <>
              {t("tripDetails.timeline.titleStart", "Votre itinéraire")}{" "}
              <em>{t("tripDetails.timeline.titleHighlight", "jour par jour")}</em>
            </>
          }
          subtitle={t(
            "tripDetails.timeline.subtitle",
            "Chaque étape du circuit, avec son hébergement, ses activités et les options à ajouter."
          )}
          className={styles.sectionHeader}
        />
        {items.length > 0 && (
          <dl className={styles.stats}>
            {stats.map((stat) => (
              <div key={stat.label} className={styles.stat}>
                <dt className={styles.statLabel}>{stat.label}</dt>
                <dd className={styles.statValue}>{stat.value}</dd>
              </div>
            ))}
          </dl>
        )}
      </div>

      {items.length > 0 ? (
        <ol className={styles.timeline}>
          {items.map((trip, index) => {
            const stepDate = splitStepDate(stepDateByStepId.get(trip?.id)?.date);
            const location = `${trip?.city ?? ""}${trip?.country ? `, ${trip.country}` : ""}`.trim();
            const title = trip?.ranking?.title || location || t("TripDetails");
            const metaLabel =
              index === 0
                ? t("tripDetails.timeline.start", "Début du voyage")
                : index === items.length - 1
                  ? t("tripDetails.timeline.end", "Fin du voyage")
                  : undefined;

            return (
              <li key={trip?.id} className={styles.step}>
                <div className={styles.date} aria-hidden={!stepDate}>
                  {stepDate && (
                    <>
                      <span className={styles.day}>{stepDate.day}</span>
                      <span className={styles.month}>{stepDate.month}</span>
                      <span className={styles.weekday}>{stepDate.weekday}</span>
                    </>
                  )}
                </div>
                <span className={styles.node} aria-hidden="true" />
                <div className={styles.content}>
                  <TripProgramCard
                    onClick={() => openModal(trip, index)}
                    ariaLabel={`${t("View_Details")} - ${title}`.trim()}
                    imageSrc={trip?.picture?.link || undefined}
                    imageAlt={location || title}
                    locationLabel={location}
                    eyebrow={stepDate?.full}
                    metaLabel={metaLabel}
                    title={title}
                    subtitle={trip?.hosting?.name || ""}
                    stars={trip?.hosting?.stars}
                    hotelLabel={t("Accommodation")}
                    hotelStatus={trip?.hosting?.name ? t("tripDetails.timeline.included", "Inclus") : undefined}
                    activitiesLabel={t("Activities")}
                    includedItems={titlesOf(getTripActivities(trip))}
                    optionsLabel={t("tripDetails.timeline.options", "Options")}
                    optionsItems={titlesOf(trip?.options)}
                    description={trip?.description || ""}
                    descriptionLines={3}
                    stepNumber={index + 1}
                    stepTotal={items.length}
                    stepLabel={t("tripDetails.timeline.step", "Étape")}
                    ctaLabel={t("commons.buttons.personalize")}
                    ctaTooltip={t("commons.buttons.personalize")}
                    ctaIconName="iconPlus"
                    onCtaClick={() => openModal(trip, index)}
                    footerLeft={
                      <AcquisitionsHelper tripProgramId={trip?.id} acquisitions={acquisitions} />
                    }
                  />
                </div>
              </li>
            );
          })}
        </ol>
      ) : (
        <div className={styles.empty}>
          <Text size="sm">{t("No trips available")}</Text>
        </div>
      )}

      <ModalTripTimelineItem
        tripProgramId={selectedTrip?.trip?.id}
        city={selectedTrip?.trip?.ranking?.title || selectedTrip?.trip?.city}
        picture={selectedTrip?.trip?.picture?.link}
        description={selectedTrip?.trip?.description}
        isOpen={!!selectedTrip}
        activities={getTripActivities(selectedTrip?.trip)}
        options={selectedTrip?.trip?.options || []}
        acquisitions={acquisitions}
        setAcquisitions={setAcquisitions}
        hostingName={selectedTrip?.trip?.hosting?.name}
        hostingStars={selectedTrip?.trip?.hosting?.stars}
        stepNumber={Number(selectedTrip?.index) + 1}
        stepTotal={items.length}
        dateLabel={splitStepDate(stepDateByStepId.get(selectedTrip?.trip?.id)?.date)?.full}
        onClose={closeModal}
      />
    </section>
  );
};

export default TripDetails;
